import { MutableRefObject, ReactNode, useMemo, useRef, useState } from 'react';
import { ImmerFuncs } from '../../../external/immer/immer.funcs';
import { IdFuncs } from '../../core/id/id.funcs';
import { EffectContext } from './effect.context';
import { ModalEffect } from './modal/modal-effect';
import { ModalEffectApi } from './modal/modal-effect.api';
import { PopupController } from './popup/popup-controller';
import { PopupEffectApi } from './popup/popup-effect.api';

type EffectItem = {
  component: ReactNode;
  key: string;
};

export function EffectContainer({ children }: { children: React.ReactNode }) {
  const popupControllerRef = useRef<PopupEffectApi>(null) as MutableRefObject<PopupEffectApi>;
  const [items, setItems] = useState<EffectItem[]>([]);

  const api = useMemo(() => {
    return {
      popupApi: {
        push: (...props: Parameters<PopupEffectApi['push']>) => {
          popupControllerRef.current?.push(...props);
        },
        pop: () => {
          popupControllerRef.current?.pop();
        },
      },
      modalApi: {
        push: (...props: Parameters<ModalEffectApi['push']>) => {
          const newItems = ImmerFuncs.produce(items, (draft) => {
            draft.push({
              component: props[0].component,
              key: IdFuncs.generate(6),
            });
          });
          setItems(newItems);
        },
        pop: () => {
          const newItems = ImmerFuncs.produce(items, (draft) => {
            draft.pop();
          });
          setItems(newItems);
        },
      },
    };
  }, []);

  return (
    <EffectContext.Provider value={api}>
      <div>{children}</div>
      {items.map((item, index) => (
        <ModalEffect
          key={item.key}
          component={item.component}
          zIndex={index * 100}
          willLeave={() => {
            api.modalApi.pop();
          }}
        />
      ))}
      <PopupController apiRef={popupControllerRef} />
    </EffectContext.Provider>
  );
}
